/**
 * Gate for the EqualWeb accessibility overlay (ENG-4334): UK customers only,
 * default-on, with a per-customer CorePlatform kill-switch flag. The flag string is
 * hand-synced across services — keep in parity with EFG's `KNOWN_FLAGS` allowlist
 * (the resident-facing feature-flags endpoint) and the CorePlatform flag key.
 */
export const EQUAL_WEB_DISABLED_FLAG = 'engageEqualWebDisabled';

/**
 * Per-browser kill-switch cookie (ENG-4510), so support can restore resident login on one
 * machine by running
 * `document.cookie = 'engageEqualWebDisabled=1; path=/; max-age=2592000'` and reloading.
 * The `max-age` (30 days) outlives a browser restart but still expires, handing the
 * resident their overlay back.
 *
 * Kept in production deliberately as a support tool. It only affects the browser it is set
 * in; a same-site script could set it too, but such a script can already rewrite the page,
 * so this adds no new capability. Its value is independent of the CorePlatform flag key.
 */
export const EQUAL_WEB_DISABLED_COOKIE = 'engageEqualWebDisabled';

const EQUAL_WEB_DISABLED_COOKIE_VALUES: ReadonlySet<string> = new Set(['1', 'true']);

export const EQUAL_WEB_COUNTRY = 'GB';

interface ShouldLoadEqualWebInput {
  country: string | undefined;
  sitekey: string | null | undefined;
  featureFlags: Record<string, boolean>;
  isEmbed: boolean;
  isDisabledByCookie: boolean;
}

export const hasEqualWebDisabledCookie = (cookieHeader: string): boolean =>
  cookieHeader.split(';').some((pair) => {
    const [name, value = ''] = pair.trim().split('=');
    return name === EQUAL_WEB_DISABLED_COOKIE && EQUAL_WEB_DISABLED_COOKIE_VALUES.has(value);
  });

export const shouldLoadEqualWeb = ({
  country,
  sitekey,
  featureFlags,
  isEmbed,
  isDisabledByCookie,
}: ShouldLoadEqualWebInput): boolean => {
  if (country !== EQUAL_WEB_COUNTRY) return false;
  // No sitekey configured means the overlay is off for the whole environment.
  if (!sitekey) return false;
  // Embedded surfaces belong to the host page, which owns its own accessibility tooling.
  if (isEmbed) return false;
  // Kill-switch is fail-open: a flag-fetch failure yields {} (see fetchFeatureFlags),
  // which must not silently remove an accessibility affordance — only an explicit true does.
  if (featureFlags[EQUAL_WEB_DISABLED_FLAG] === true) return false;
  if (isDisabledByCookie) return false;
  return true;
};
